// src/config.js
// Central config for backend API

// Smart backend URL detection
const DEFAULT_PORT = 5050;

// This browser is on the same machine as the backend
const isLoopback = (hostname) => hostname === 'localhost' || hostname === '127.0.0.1';

// Same private ranges the backend accepts in its CORS check. A LAN IP means
// another device on the same network, not this machine.
const LAN_IP_PATTERN = /^(192\.168\.\d+\.\d+|10\.\d+\.\d+\.\d+|172\.(1[6-9]|2\d|3[01])\.\d+\.\d+)$/;

// Dynamically determine backend URL based on how the frontend is accessed
const getBackendUrl = () => {
  // If environment variable is set, use it
  if (process.env.REACT_APP_BACKEND_URL) {
    return process.env.REACT_APP_BACKEND_URL;
  }

  // If accessing via localhost, use localhost for backend
  if (isLoopback(window.location.hostname)) {
    return `http://localhost:${DEFAULT_PORT}`;
  }

  // For production/deployed environments with nginx proxy
  // Check if accessing via domain (no IP pattern)
  const hostname = window.location.hostname;
  const isDomain = !hostname.match(/^\d+\.\d+\.\d+\.\d+$/); // Not an IP address

  if (isDomain) {
    // Domain-based access (gibbons.boca.mx, demo.boca.mx, etc.)
    // Use nginx proxy path (same protocol/host)
    return `${window.location.protocol}//${window.location.host}`;
  }

  // Local network IP access (e.g., 192.168.x.x)
  // Use the hostname with backend port
  return `${window.location.protocol}//${hostname}:${DEFAULT_PORT}`;
};

export const BACKEND_URL = getBackendUrl();

// Fallback for the receipt QR code when a tenant has no qrCodeUrl setting.
// The current origin is the tenant's own site, whichever name it answers on.
export const DEFAULT_QR_CODE_URL = window.location.origin;

// Tenant identity
// Every tenant answers on two names: the original boca.mx zone and the
// bocapos.app zone we control. Both names reach the same database, so the
// frontend has to treat them as the same tenant.
const TENANTS_BY_HOSTNAME = {
  'gibbons.boca.mx': 'gibbons',
  'gibbons.bocapos.app': 'gibbons',
  'demo.boca.mx': 'demo',
  'demo.bocapos.app': 'demo',
  'dev.boca.mx': 'dev',
  'dev.bocapos.app': 'dev',
  'nonna.boca.mx': 'nonna',
  'nonna.bocapos.app': 'nonna',
  'lura.bocapos.app': 'lura',
};

// Resolve the current hostname into a tenant and the two environment facts
// the rest of the app asks about. They are deliberately separate: see the
// comments on each flag below.
// An unrecognised hostname is treated as production with no tenant, so the
// debug panel, the PIN skip and OpenPay all stay off.
export const getTenantInfo = (hostname = window.location.hostname) => ({
  tenant: TENANTS_BY_HOSTNAME[hostname] || null,

  // Where the backend lives. Loopback or a LAN IP, because a phone or tablet
  // on the same network still reaches a developer machine's backend.
  isLocalNetwork: isLoopback(hostname) || LAN_IP_PATTERN.test(hostname),

  // This machine only. LAN is excluded on purpose, see areDevAffordancesEnabled.
  isDeveloperMachine: isLoopback(hostname),
});

// The dev tenant on either of its names. Does not include developer machines.
export const isDevTenant = () => getTenantInfo().tenant === 'dev';

// The demo tenant on either of its names
export const isDemoTenant = () => getTenantInfo().tenant === 'demo';

// How to reach the backend, not who is allowed to see what. Anything that
// gates behaviour wants areDevAffordancesEnabled instead.
export const isLocalNetworkHost = () => getTenantInfo().isLocalNetwork;

// Development affordances: the testdev PIN skip, the demo credential list and
// OpenPay. This machine or the dev tenant on either of its names, and nothing
// else.
// Do not add the LAN ranges here. A LAN IP is any device on the same network,
// including a customer's phone on the restaurant wifi, and the PIN skip and
// the credential list have no backend gate behind them, so widening this hands
// those out for real. The backend OpenPay guard allows only localhost,
// 127.0.0.1 and the two dev names, so a wider check here would also offer a
// payment path the backend then rejects. Reaching a backend over the LAN is a
// separate question, answered by isLocalNetworkHost.
export const areDevAffordancesEnabled = () => {
  const { tenant, isDeveloperMachine } = getTenantInfo();
  return isDeveloperMachine || tenant === 'dev';
};

// OpenPay is only enabled on the dev tenant and developer machines
export const isOpenPayEnabled = () => areDevAffordancesEnabled();
